/**
 * User Type Enum
 *
 * Defines the types of external users in the system.
 * These are frontend-specific values used for user registration.
 *
 * Types:
 * - PRESCRIBER: Doctor/medical professional
 * - DISPENSER: Pharmacist
 * - INSTITUTION_RESPONSIBLE: Institutional user
 */
import { intl } from "dmsbasereact";

// Base enum with values only (labels are computed at runtime)
export const USER_TYPE_VALUES = {
  PRESCRIBER: "PRESCRIBER",
  DISPENSER: "DISPENSER",
  INSTITUTION_RESPONSIBLE: "INSTITUTION_RESPONSIBLE",
  DISTRIBUTOR_RESPONSIBLE: "DISTRIBUTOR_RESPONSIBLE",
};

// Labels for internal Eurofarma / regulator / API-manager roles that /person/my_profile
// now surfaces in `user_types`. Values match backend UserRoleEnumApp names.
const INTERNAL_ROLE_LABEL_KEYS = {
  EUROFARMA_GESTAO_LENA: { key: "UserType.GestaoLena", default: "Gestão Lena" },
  EUROFARMA_DIRETORIA: { key: "UserType.Diretoria", default: "Diretoria" },
  EUROFARMA_REGULATORIO: { key: "UserType.Regulatorio", default: "Regulatório" },
  EUROFARMA_COMERCIAL: { key: "UserType.Comercial", default: "Comercial" },
  EUROFARMA_GARANTIA: { key: "UserType.Garantia", default: "Garantia da Qualidade" },
  EUROFARMA_CENTRAL: { key: "UserType.Central", default: "Central" },
  EUROFARMA_FARMA_LENA: { key: "UserType.FarmaLena", default: "Farmacovigilância" },
  API_KEY_MANAGER: { key: "UserType.ApiKeyManager", default: "API Key Manager" },
  ORGAO_REGULADOR_AUDITOR: { key: "UserType.OrgaoReguladorAuditor", default: "Auditor (Órgão Regulador)" },
};

// Function to get labels at runtime (for proper i18n)
export const getUserTypeLabel = (value) => {
  switch (value) {
    case USER_TYPE_VALUES.PRESCRIBER:
      return intl.get("Prescriber").d("Prescriber");
    case USER_TYPE_VALUES.DISPENSER:
      return intl.get("Dispenser").d("Dispenser");
    case USER_TYPE_VALUES.INSTITUTION_RESPONSIBLE:
      return intl.get("InstitutionResponsible").d("Institution Responsible");
    case USER_TYPE_VALUES.DISTRIBUTOR_RESPONSIBLE:
      return intl.get("DistributorResponsible").d("Distributor Responsible");
    default: {
      const internal = INTERNAL_ROLE_LABEL_KEYS[value];
      if (internal) return intl.get(internal.key).d(internal.default);
      return value;
    }
  }
};

// UserTypeEnum with getter for labels to ensure i18n works at render time
export const UserTypeEnum = {
  PRESCRIBER: {
    get label() { return intl.get("Prescriber").d("Prescriber"); },
    value: USER_TYPE_VALUES.PRESCRIBER,
  },
  DISPENSER: {
    get label() { return intl.get("Dispenser").d("Dispenser"); },
    value: USER_TYPE_VALUES.DISPENSER,
  },
  INSTITUTION_RESPONSIBLE: {
    get label() { return intl.get("InstitutionResponsible").d("Institution Responsible"); },
    value: USER_TYPE_VALUES.INSTITUTION_RESPONSIBLE,
  },
  DISTRIBUTOR_RESPONSIBLE: {
    get label() { return intl.get("DistributorResponsible").d("Distributor Responsible"); },
    value: USER_TYPE_VALUES.DISTRIBUTOR_RESPONSIBLE,
  },
};

/**
 * Get user type options for select components
 * @returns {Array} Array of {value, label} objects
 */
export const getUserTypeOptions = () => {
  return Object.values(UserTypeEnum).map(type => ({
    value: type.value,
    label: type.label
  }));
};

/**
 * Map frontend user type values to backend type names
 * @param {string} frontendType - Frontend type value (e.g., "PRESCRIBER")
 * @returns {string} Backend type name (e.g., "doctor")
 */
export const mapUserTypeToBackend = (frontendType) => {
  const mapping = {
    [USER_TYPE_VALUES.PRESCRIBER]: "doctor",
    [USER_TYPE_VALUES.DISPENSER]: "pharmacist",
    [USER_TYPE_VALUES.INSTITUTION_RESPONSIBLE]: "institution_responsible",
    [USER_TYPE_VALUES.DISTRIBUTOR_RESPONSIBLE]: "distributor_responsible",
  };
  return mapping[frontendType] || frontendType.toLowerCase();
};

/**
 * Map backend type names to frontend user type values
 * @param {string} backendType - Backend type name (e.g., "doctor")
 * @returns {string} Frontend type value (e.g., "PRESCRIBER")
 */
export const mapBackendTypeToFrontend = (backendType) => {
  const mapping = {
    "doctor": USER_TYPE_VALUES.PRESCRIBER,
    "pharmacist": USER_TYPE_VALUES.DISPENSER,
    "institution_responsible": USER_TYPE_VALUES.INSTITUTION_RESPONSIBLE,
    "distributor_responsible": USER_TYPE_VALUES.DISTRIBUTOR_RESPONSIBLE,
  };
  if (mapping[backendType]) return mapping[backendType];
  // Internal role names (EUROFARMA_*, API_KEY_MANAGER, ORGAO_REGULADOR_AUDITOR) are
  // already uppercase from the backend and serve as their own "type" identifier here.
  if (backendType && backendType === backendType.toUpperCase()) return backendType;
  return backendType.toUpperCase();
};

/**
 * Check if a user type array includes prescriber (doctor)
 * @param {Array} userTypes - Array of frontend type values
 * @returns {boolean}
 */
export const includesPrescriber = (userTypes) => {
  return userTypes?.includes(USER_TYPE_VALUES.PRESCRIBER);
};

/**
 * Check if a user type array includes dispenser (pharmacist)
 * @param {Array} userTypes - Array of frontend type values
 * @returns {boolean}
 */
export const includesDispenser = (userTypes) => {
  return userTypes?.includes(USER_TYPE_VALUES.DISPENSER);
};

/**
 * Check if a user type array includes institution responsible
 * @param {Array} userTypes - Array of frontend type values
 * @returns {boolean}
 */
export const includesInstitutionResponsible = (userTypes) => {
  return userTypes?.includes(USER_TYPE_VALUES.INSTITUTION_RESPONSIBLE);
};

/**
 * Check if a user type array includes distributor responsible
 * @param {Array} userTypes - Array of frontend type values
 * @returns {boolean}
 */
export const includesDistributorResponsible = (userTypes) => {
  return userTypes?.includes(USER_TYPE_VALUES.DISTRIBUTOR_RESPONSIBLE);
};

/**
 * Forbidden combinations of user types.
 * Each entry is an array of two types that cannot be selected together.
 *
 * Rules:
 * 1. Prescriber cannot be combined with Dispenser
 * 2. Institution Responsible cannot be combined with Prescriber
 * 3. Distributor Responsible is mutually exclusive (cannot be combined with any other type)
 */
export const FORBIDDEN_COMBINATIONS = [
  [USER_TYPE_VALUES.PRESCRIBER, USER_TYPE_VALUES.DISPENSER],
  [USER_TYPE_VALUES.INSTITUTION_RESPONSIBLE, USER_TYPE_VALUES.PRESCRIBER],
  [USER_TYPE_VALUES.DISPENSER, USER_TYPE_VALUES.DISTRIBUTOR_RESPONSIBLE],
  [USER_TYPE_VALUES.PRESCRIBER, USER_TYPE_VALUES.DISTRIBUTOR_RESPONSIBLE],
  [USER_TYPE_VALUES.INSTITUTION_RESPONSIBLE, USER_TYPE_VALUES.DISTRIBUTOR_RESPONSIBLE],
];

/**
 * Check if selecting a new type would create a forbidden combination
 * @param {Array} currentTypes - Currently selected user types
 * @param {string} newType - Type being considered for selection
 * @returns {boolean} True if the combination is forbidden
 */
export const isForbiddenCombination = (currentTypes, newType) => {
  if (!currentTypes || currentTypes.length === 0) return false;

  for (const [type1, type2] of FORBIDDEN_COMBINATIONS) {
    // Check if selecting newType would create a forbidden pair
    if (newType === type1 && currentTypes.includes(type2)) return true;
    if (newType === type2 && currentTypes.includes(type1)) return true;
  }
  return false;
};

/**
 * Get the conflicting type for a given type based on current selection
 * @param {Array} currentTypes - Currently selected user types
 * @param {string} typeToCheck - Type to check for conflicts
 * @returns {string|null} The conflicting type label or null if no conflict
 */
export const getConflictingType = (currentTypes, typeToCheck) => {
  if (!currentTypes || currentTypes.length === 0) return null;

  for (const [type1, type2] of FORBIDDEN_COMBINATIONS) {
    if (typeToCheck === type1 && currentTypes.includes(type2)) {
      return getUserTypeLabel(type2);
    }
    if (typeToCheck === type2 && currentTypes.includes(type1)) {
      return getUserTypeLabel(type1);
    }
  }
  return null;
};

/**
 * Map all user roles to all corresponding user type values (no priority, returns all).
 * Use this when you need to know every type a user has (e.g., institution category filtering).
 *
 * @param {Array<string>} roles - Array of user roles (e.g., ["PHARMACIST", "INSTITUTION_RESPONSIBLE"])
 * @returns {Array<string>} All matching USER_TYPE_VALUES
 */
export const mapRolesToUserTypes = (roles) => {
  if (!roles || roles.length === 0) return [];
  const types = [];
  if (roles.includes("DISTRIBUTOR_RESPONSIBLE")) types.push(USER_TYPE_VALUES.DISTRIBUTOR_RESPONSIBLE);
  if (roles.includes("DOCTOR")) types.push(USER_TYPE_VALUES.PRESCRIBER);
  if (roles.includes("PHARMACIST")) types.push(USER_TYPE_VALUES.DISPENSER);
  if (roles.includes("INSTITUTION_RESPONSIBLE")) types.push(USER_TYPE_VALUES.INSTITUTION_RESPONSIBLE);
  return types;
};

/**
 * Map user roles (from logged-in user) to user type for institution category filtering.
 * Priority order determines which user type takes precedence when user has multiple roles.
 *
 * @param {Array<string>} roles - Array of user roles (e.g., ["DOCTOR", "INSTITUTION_RESPONSIBLE"])
 * @returns {string|null} The corresponding USER_TYPE_VALUE or null for admin users
 */
export const mapRolesToUserType = (roles) => {
  if (!roles || roles.length === 0) return null;

  // Priority order for mapping (first match wins)
  // DISTRIBUTOR_RESPONSIBLE has highest priority as it has locked categories
  if (roles.includes("DISTRIBUTOR_RESPONSIBLE")) {
    return USER_TYPE_VALUES.DISTRIBUTOR_RESPONSIBLE;
  }
  // DOCTOR maps to PRESCRIBER (locked category)
  if (roles.includes("DOCTOR")) {
    return USER_TYPE_VALUES.PRESCRIBER;
  }
  // PHARMACIST maps to DISPENSER
  if (roles.includes("PHARMACIST")) {
    return USER_TYPE_VALUES.DISPENSER;
  }
  // INSTITUTION_RESPONSIBLE maps directly
  if (roles.includes("INSTITUTION_RESPONSIBLE")) {
    return USER_TYPE_VALUES.INSTITUTION_RESPONSIBLE;
  }
  // Admin roles (EUROFARMA_GESTAO_LENA, API_KEY_MANAGER) return null to use default rules
  return null;
};

/**
 * Get user type from current session token scopes.
 * Uses mapRolesToUserType to determine the user type based on the logged-in user's roles.
 *
 * @param {Function} getToken - Function to get token from session (from dmsbasereact)
 * @returns {string|null} The corresponding USER_TYPE_VALUE or null for admin users
 */
export const getUserTypeFromToken = (getToken) => {
  try {
    const token = getToken();
    if (!token || !token.scopes) return null;
    return mapRolesToUserType(token.scopes);
  } catch (e) {
    return null;
  }
};
